import {
  useDocumentControllerGetPublishedConsult,
  useDocumentControllerGetValidationReview,
} from '@app/api-client';
import type {
  DocumentDto,
  DocumentExtension,
  DocumentValidationReviewDto,
  DocumentValueDto,
} from '@app/contracts';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, BookMarked, FileQuestion, Lock, MessageSquareWarning } from 'lucide-react';
import { useMemo } from 'react';
import { ToolNeedsWait } from '../components/tools/ToolNeedsWait.js';
import { ToolPaOperational } from '../components/tools/ToolPaOperational.js';
import { ToolPestel } from '../components/tools/ToolPestel.js';
import { ToolSimpleRecord } from '../components/tools/ToolSimpleRecord.js';
import { ToolSwot } from '../components/tools/ToolSwot.js';
import { useDocument, useDocumentValues } from '../hooks/useDocuments.js';
import { computeValidationHighlights } from '../utils/validation-diff.js';

interface ToolDispatchProps {
  document: DocumentDto;
  values: DocumentValueDto[];
  openDocumentRights: boolean;
  readOnly: boolean;
  matrixHighlightKeys: Set<string>;
  changedValueIds: Set<string>;
  bodyHighlight: boolean;
  validationReview?: DocumentValidationReviewDto;
}

function ToolDispatch({
  document,
  values,
  openDocumentRights,
  readOnly,
  matrixHighlightKeys,
  changedValueIds,
  bodyHighlight,
  validationReview,
}: ToolDispatchProps) {
  const ext = document.extension as DocumentExtension;
  switch (ext) {
    case 'swot':
      return (
        <ToolSwot
          document={document}
          value={values[0]}
          readOnly={readOnly}
          highlightItemKeys={matrixHighlightKeys}
          validationReview={validationReview}
        />
      );
    case 'pestel':
      return (
        <ToolPestel
          document={document}
          value={values[0]}
          readOnly={readOnly}
          highlightItemKeys={matrixHighlightKeys}
          validationReview={validationReview}
        />
      );
    case 'needsWait':
      return (
        <ToolNeedsWait
          document={document}
          values={values}
          autoOpenDocumentRights={openDocumentRights}
          readOnly={readOnly}
          changedValueIds={changedValueIds}
          validationReview={validationReview}
        />
      );
    case 'paOperational':
      return (
        <ToolPaOperational
          document={document}
          values={values}
          readOnly={readOnly}
          changedValueIds={changedValueIds}
          validationReview={validationReview}
        />
      );
    case 'auditPlanning':
    case 'auditReport':
    case 'nonConformity':
    case 'managementReview':
      return (
        <ToolSimpleRecord
          document={document}
          value={values[0]}
          readOnly={readOnly}
          highlightBody={bodyHighlight}
          validationReview={validationReview}
        />
      );
    default: {
      const _exhaustive: never = ext;
      return (
        <div className="flex flex-col items-center gap-3 py-16 text-slate-400">
          <FileQuestion className="h-10 w-10" />
          <p className="text-sm">Type de document non reconnu : {document.extension}</p>
        </div>
      );
    }
  }
}

interface Props {
  id: string;
  openDocumentRights?: boolean;
  /** Affiche le cliché de la publication active (lecture seule), pas le document vivant. */
  viewPublished?: boolean;
}

export function DocumentViewPage({ id, openDocumentRights = false, viewPublished = false }: Props) {
  const {
    data: document,
    isLoading: loadingDoc,
    error: docError,
  } = useDocument(id, {
    enabled: !viewPublished,
  });
  const { data: values = [], isLoading: loadingValues } = useDocumentValues(
    id,
    !viewPublished && Boolean(id),
  );
  const { data: review, isLoading: loadingReview } = useDocumentControllerGetValidationReview(id, {
    query: { enabled: !viewPublished && Boolean(id) },
  });
  const {
    data: publishedConsult,
    isLoading: loadingPublishedConsult,
    error: publishedConsultError,
  } = useDocumentControllerGetPublishedConsult(id, {
    query: { enabled: viewPublished && Boolean(id) },
  });

  const editLocked = !viewPublished && review?.editLocked === true;
  const displayDocument = viewPublished
    ? publishedConsult?.displayDocument
    : editLocked && review?.displayDocument
      ? review.displayDocument
      : document;
  const displayValues = viewPublished
    ? (publishedConsult?.displayValues ?? [])
    : editLocked && review?.displayValues && review.displayValues.length > 0
      ? review.displayValues
      : values;

  const highlights = useMemo(() => {
    if (viewPublished || !displayDocument || !displayValues.length) {
      return {
        matrixItemKeys: new Set<string>(),
        changedValueIds: new Set<string>(),
      };
    }
    return computeValidationHighlights(
      displayDocument.extension,
      displayValues as DocumentValueDto[],
      review?.baselineSnapshot ?? null,
    );
  }, [viewPublished, displayDocument, displayValues, review?.baselineSnapshot]);

  const bodyHighlight =
    !viewPublished &&
    displayValues.length > 0 &&
    highlights.changedValueIds.has(displayValues[0]?.id ?? '');

  const refusalRemarks = review?.validations.filter((v) => v.validation === 2) ?? [];
  const showRefusalBanner = !viewPublished && !editLocked && refusalRemarks.length > 0;

  const loadingMain = viewPublished
    ? loadingPublishedConsult
    : loadingDoc || loadingValues || loadingReview;

  if (loadingMain) {
    return (
      <div className="flex justify-center py-16">
        <span className="relative flex h-10 w-10" aria-hidden="true">
          <span className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
          <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-transparent border-t-emerald-500" />
        </span>
      </div>
    );
  }

  if (viewPublished) {
    if (publishedConsultError || !displayDocument) {
      return (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Publication introuvable, droit de consultation insuffisant ou erreur de chargement.
        </div>
      );
    }
  } else if (docError || !document || !displayDocument) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        Document introuvable ou erreur de chargement.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <Link
          to={viewPublished ? '/publication/publie' : '/documents'}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600"
        >
          <ArrowLeft className="h-4 w-4" />
          {viewPublished ? 'Retour aux documents publiés' : 'Retour aux documents'}
        </Link>
      </div>
      {viewPublished && publishedConsult ? (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
          <BookMarked className="h-4 w-4 shrink-0 text-emerald-700" />
          <span>
            <strong>Version publiée (lecture seule).</strong> Le contenu affiché est celui figé dans
            la publication <strong>v{publishedConsult.version}</strong>
            {publishedConsult.publishName?.trim()
              ? ` — ${publishedConsult.publishName.trim()}`
              : ''}
            . Publié le{' '}
            {new Date(publishedConsult.publishedAt).toLocaleString('fr-FR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
            .
          </span>
        </div>
      ) : null}
      {editLocked ? (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <Lock className="h-4 w-4 shrink-0" />
          <span>
            Ce document est en <strong>lecture seule</strong> : une soumission est en attente de
            validation. Les zones surlignées indiquent un changement par rapport à la dernière
            version publiée (lorsque celle-ci est disponible au même format).
          </span>
        </div>
      ) : null}
      {showRefusalBanner ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950">
          <p className="flex items-center gap-2 font-semibold text-rose-900">
            <MessageSquareWarning className="h-4 w-4 shrink-0" aria-hidden />
            Dernière soumission refusée
          </p>
          <p className="mt-1 text-rose-900/90">
            Corrigez le document ci-dessous puis utilisez « Soumettre » pour renvoyer en validation.
          </p>
          <ul className="mt-3 space-y-2">
            {refusalRemarks.map((v) => (
              <li
                key={v.id}
                className="rounded-lg border border-rose-100 bg-white/90 px-3 py-2 text-slate-800"
              >
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  Bloc valeur{' '}
                  <span className="font-mono normal-case">{v.documentValueId.slice(0, 8)}…</span>
                </p>
                {v.content?.trim() ? (
                  <p className="mt-1 whitespace-pre-wrap text-sm">{v.content}</p>
                ) : (
                  <p className="mt-1 text-sm italic text-slate-500">Refus sans commentaire.</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ToolDispatch
        document={displayDocument}
        values={displayValues as DocumentValueDto[]}
        openDocumentRights={viewPublished ? false : openDocumentRights}
        readOnly={editLocked || viewPublished}
        matrixHighlightKeys={highlights.matrixItemKeys}
        changedValueIds={highlights.changedValueIds}
        bodyHighlight={bodyHighlight}
        validationReview={
          viewPublished
            ? undefined
            : ((review ?? undefined) as DocumentValidationReviewDto | undefined)
        }
      />
    </div>
  );
}
